/**
 * Polish display formatting: pure functions, `Intl` with `pl-PL` and `Europe/Warsaw`, formatters cached.
 * Empty, null or invalid input gives '' (never NaN, never throws): the template owns the placeholder.
 * Money, plural and date outputs match `data_table_formats.js` for the same inputs.
 */

const LOCALE = 'pl-PL'
const TIME_ZONE = 'Europe/Warsaw'

const cache = new Map()
/** @param {string} key @param {() => Intl.NumberFormat | Intl.DateTimeFormat} make */
function cached(key, make) {
  if (!cache.has(key)) cache.set(key, make())
  return cache.get(key)
}

const decimalFormat = (decimals) =>
  cached(`n${decimals}`, () => new Intl.NumberFormat(LOCALE, { minimumFractionDigits: decimals, maximumFractionDigits: decimals, useGrouping: 'always' }))

const clampDecimals = (decimals, fallback) => (Number.isInteger(decimals) && decimals >= 0 && decimals <= 20 ? decimals : fallback)

const DECIMAL_STRING = /^-?\d+(\.\d+)?$/

/**
 * A number, or a decimal string from DRF, as something Intl formats. A decimal string stays a string so Intl
 * rounds the exact decimal; anything else goes through Number. Returns null when it is not a finite number.
 */
function numeric(value) {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  if (typeof value !== 'string') return null
  const text = value.trim()
  if (text === '') return null
  if (DECIMAL_STRING.test(text)) return text
  const number = Number(text)
  return Number.isFinite(number) ? number : null
}

/** `1234.5` or `'1234.50'` -> `1 234,50 zł` (NBSP grouping, plain space before the symbol). */
export function money(value, { currency = 'PLN', decimals = 2 } = {}) {
  const input = numeric(value)
  if (input === null) return ''
  const places = clampDecimals(decimals, 2)
  const symbol = cached(`c${currency}`, () => new Intl.NumberFormat(LOCALE, { style: 'currency', currency }))
    .formatToParts(0)
    .find((part) => part.type === 'currency')?.value
  return `${decimalFormat(places).format(input)} ${symbol ?? currency}`
}

/** `1234567` -> `1 234 567`. Default decimals: 0. */
export function number(value, { decimals = 0 } = {}) {
  const input = numeric(value)
  return input === null ? '' : decimalFormat(clampDecimals(decimals, 0)).format(input)
}

/** The input is a 0-100 number (`23` -> `23%`, `25.55` with decimals 1 -> `25,6%`), NOT a fraction. */
export function percent(value, { decimals = 0 } = {}) {
  const text = number(value, { decimals })
  return text === '' ? '' : `${text}%`
}

/** Seconds -> `2 min 5 s`, `1 h 2 min 5 s`, `59 s`. Whole seconds; negative or invalid gives ''. */
export function duration(seconds) {
  const input = numeric(seconds)
  const total = input === null ? NaN : Math.round(Number(input))
  if (!Number.isFinite(total) || total < 0) return ''
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  const parts = []
  if (h) parts.push(`${h} h`)
  if (m) parts.push(`${m} min`)
  if (s || parts.length === 0) parts.push(`${s} s`)
  return parts.join(' ')
}

const BYTE_UNITS = ['B', 'KB', 'MB', 'GB', 'TB']

/** `1234567` -> `1,2 MB` (1024 steps, at most one decimal; bytes are whole). */
export function bytes(value) {
  const input = numeric(value)
  const size = input === null ? NaN : Number(input)
  if (!Number.isFinite(size) || size < 0) return ''
  let unit = 0
  let scaled = size
  while (scaled >= 1024 && unit < BYTE_UNITS.length - 1) {
    scaled /= 1024
    unit += 1
  }
  const text = cached(`b${unit ? 1 : 0}`, () => new Intl.NumberFormat(LOCALE, { maximumFractionDigits: unit ? 1 : 0 })).format(scaled)
  return `${text} ${BYTE_UNITS[unit]}`
}

/**
 * The Polish form of the word for `n`: `plural(2, ['lead', 'leady', 'leadów'])` -> `leady`. Returns the word only;
 * prefix the number yourself (`${n} ${plural(n, forms)}`, same text as data_table_formats `plural`).
 * @param {unknown} n
 * @param {[string, string, string]} forms one, few, many
 */
export function plural(n, forms) {
  const input = numeric(n)
  const count = input === null ? NaN : Number(input)
  if (!Number.isFinite(count) || !Array.isArray(forms)) return ''
  const [one, few, many] = forms
  const mod10 = count % 10
  const mod100 = count % 100
  if (count === 1) return one
  return mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14) ? few : many
}

function toDate(iso) {
  if (iso === null || iso === undefined || iso === '') return null
  const moment = iso instanceof Date ? iso : new Date(iso)
  return Number.isNaN(moment.getTime()) ? null : moment
}

const warsawParts = () =>
  cached(
    'parts',
    () =>
      new Intl.DateTimeFormat(LOCALE, {
        timeZone: TIME_ZONE,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
      }),
  )

function parts(iso) {
  const moment = toDate(iso)
  if (!moment) return null
  const out = {}
  for (const part of warsawParts().formatToParts(moment)) out[part.type] = part.value
  return out
}

/** ISO instant -> `05.10.2026` (Warsaw calendar day). */
export function date(iso) {
  const p = parts(iso)
  return p ? `${p.day}.${p.month}.${p.year}` : ''
}

/** ISO instant -> `05.10.2026, 14:30`. */
export function dateTime(iso) {
  const p = parts(iso)
  return p ? `${p.day}.${p.month}.${p.year}, ${p.hour}:${p.minute}` : ''
}

/** ISO instant -> `14:30` in Warsaw. */
export function time(iso) {
  const p = parts(iso)
  return p ? `${p.hour}:${p.minute}` : ''
}

/** ISO instant -> `2026-10-05`: the WARSAW calendar day, the grouping key for the timeline. */
export function dayKey(iso) {
  const p = parts(iso)
  return p ? `${p.year}-${p.month}-${p.day}` : ''
}
